import { httpClient } from '@/utils/httpClient';
import { outputReport, getOutputFormat, getPreOpenedWindow } from '@/utils/reportOutput';
import {
  buildAssetsCurrentlyAssignedReportHtml,
  type AssetsCurrentlyAssignedGroup,
  type AssetsCurrentlyAssignedItem,
  type AssetsCurrentlyAssignedTemplateOptions,
} from '@/reports/templates/assetsCurrentlyAssignedTemplate';
import { getPersonLabels } from '@/hooks/usePersonLabels';

interface AssetsCurrentlyAssignedRow {
  id: string;
  hand_receipt_number: string | null;
  phr_thr: 'PHR' | 'THR' | string | null;
  due_date: string | null;
  person_id: string | null;
  person_name: string | null;
  person_rank: string | null;
  catalog_id: string | null;
  lin: string | null;
  nsn: string | null;
  nomenclature: string | null;
  size: string | null;
  unit_price: number | string | null;
  recoverable_flag: string | null;
  authorized_qty: number | null;
  qty_issued: number | null;
  has_catalog_image: boolean | null;
  status: string | null;
  receipt_date: string | null;
}

const parseUnitPrice = (value: number | string | null | undefined): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const PAGE_SIZE = 1000;

interface PersonParam {
  id: string;
  displayName: string;
}

interface FetchFilters {
  personIds?: string[];
  excludePnp?: boolean;
  excludeSoc?: boolean;
  /** When true, only SOC-tagged assets are included (soc_tag = true). Used for restricted roles. */
  socOnly?: boolean;
  assetType?: 'PHR' | 'THR' | 'both';
  assetStatus?: 'active' | 'retired' | 'both';
  receiptStartDate?: string;
  receiptEndDate?: string;
}

const normalizeMemberName = (value: string | null | undefined, fallback: string): string => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : fallback;
};

const abbreviateRecoverable = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const upper = value.trim().toUpperCase();
  if (upper.includes('NON')) return 'NR';
  if (upper.includes('RECOVERABLE')) return 'R';
  return value;
};

const resolveGroupKey = (row: AssetsCurrentlyAssignedRow): string => {
  const personId = row.person_id?.trim();
  if (personId) {
    return `id:${personId}`;
  }

  return `name:${normalizeMemberName(row.person_name, 'Unknown').toLowerCase()}`;
};

async function fetchAllRows(filters?: FetchFilters): Promise<AssetsCurrentlyAssignedRow[]> {
  const allRows: AssetsCurrentlyAssignedRow[] = [];
  let lastId: string | undefined;

  while (true) {
    const searchParams = new URLSearchParams({
      order: 'person_name.asc,lin.asc,nomenclature.asc,size.asc,id.asc',
      limit: String(PAGE_SIZE),
    });
    if (filters?.personIds?.length === 1) {
      searchParams.set('person_id', `eq.${filters.personIds[0]}`);
    } else if (filters?.personIds && filters.personIds.length > 1) {
      searchParams.set('person_id', `in.(${filters.personIds.join(',')})`);
    }
    if (filters?.excludePnp) {
      searchParams.set('or', '(catalog_type.is.null,catalog_type.neq.pnp)');
    }
    if (filters?.socOnly) {
      // Restricted roles (Finance, Comptroller, HR) see only SOC assets.
      searchParams.set('soc_tag', 'eq.true');
    } else if (filters?.excludeSoc) {
      searchParams.set('soc_tag', 'eq.false');
    }
    if (filters?.assetType && filters.assetType !== 'both') {
      searchParams.set('phr_thr', `eq.${filters.assetType}`);
    }
    if (filters?.assetStatus && filters.assetStatus !== 'both') {
      searchParams.set('status', `eq.${filters.assetStatus}`);
    }
    if (filters?.receiptStartDate) {
      searchParams.set('receipt_date', `gte.${filters.receiptStartDate}`);
    }
    if (filters?.receiptEndDate) {
      searchParams.append('receipt_date', `lte.${filters.receiptEndDate}`);
    }
    if (lastId) {
      searchParams.set('id', `gt.${lastId}`);
    }

    const endpoint = `/api/rest/data/v1/assets_currently_assigned?${searchParams.toString()}`;
    const page = await httpClient.get<AssetsCurrentlyAssignedRow[]>(endpoint);
    allRows.push(...page);

    if (page.length < PAGE_SIZE) {
      break;
    }
    lastId = page[page.length - 1]!.id;
  }

  return allRows;
}

export const runAssetsCurrentlyAssignedReport = async (
  params: Record<string, unknown>,
): Promise<void> => {
  const pl = getPersonLabels(params);
  // Support both single person (legacy) and multi-person params
  const personsParam = params.persons as PersonParam[] | null | undefined;
  const personParam = params.person as PersonParam | null | undefined;

  const personIds: string[] = [];
  const personNames: string[] = [];

  if (personsParam && personsParam.length > 0) {
    for (const p of personsParam) {
      if (p.id) personIds.push(p.id);
      if (p.displayName?.trim()) personNames.push(p.displayName.trim());
    }
  } else if (personParam?.id) {
    personIds.push(personParam.id);
    if (personParam.displayName?.trim()) personNames.push(personParam.displayName.trim());
  } else {
    const legacyId = (params.personId as string | undefined)?.trim();
    if (legacyId) personIds.push(legacyId);
  }

  const excludePnp = Boolean(params.excludePnp ?? false);
  const excludeSoc = Boolean(params.excludeSoc ?? false);
  // Restricted roles always see only SOC assets — this flag is set by ReportsPage when
  // isComptroller or isHr is true and overrides any user-supplied excludeSoc value.
  const socOnly = Boolean(params.socOnly ?? false);
  const viewPictures = Boolean(params.viewPictures ?? true);
  // AC-12: assetType defaults to 'both' and assetStatus defaults to 'active' when absent,
  // reproducing today's hard-coded active-only behavior by default.
  const assetType = (params.assetType as 'PHR' | 'THR' | 'both' | undefined) ?? 'both';
  const assetStatus = (params.assetStatus as 'active' | 'retired' | 'both' | undefined) ?? 'active';
  const receiptStartDate = (params.receiptStartDate as string | undefined)?.trim() || undefined;
  const receiptEndDate = (params.receiptEndDate as string | undefined)?.trim() || undefined;

  const rows = await fetchAllRows({
    personIds: personIds.length > 0 ? personIds : undefined,
    excludePnp,
    excludeSoc,
    socOnly,
    assetType,
    assetStatus,
    receiptStartDate,
    receiptEndDate,
  });

  // Build per-member groups, consolidating like items (same catalog/nomenclature/phr_thr)
  // into a single row with summed qty and joined document numbers.
  const groupsByMember = new Map<
    string,
    { individualName: string; itemMap: Map<string, AssetsCurrentlyAssignedItem> }
  >();

  rows.forEach((row) => {
    const memberKey = resolveGroupKey(row);
    const group = groupsByMember.get(memberKey) ?? {
      individualName: normalizeMemberName(row.person_name, `Unknown ${pl.label}`),
      itemMap: new Map<string, AssetsCurrentlyAssignedItem>(),
    };

    const phrThr = row.phr_thr ?? '';
    const nomenclature = row.nomenclature ?? '—';
    const lin = row.lin ?? '';
    const nsn = row.nsn ?? '';
    const itemKey = `${row.catalog_id ?? ''}|${nomenclature}|${lin}|${nsn}|${phrThr}`;

    const existing = group.itemMap.get(itemKey);
    if (existing) {
      // Consolidate: sum qty, join document numbers, take earliest due date
      const newQty = row.qty_issued ?? 0;
      existing.quantityIssued = (existing.quantityIssued ?? 0) + newQty;

      if (row.due_date) {
        if (!existing.dueDate || row.due_date < existing.dueDate) {
          existing.dueDate = row.due_date;
        }
      }

      if (row.receipt_date) {
        if (!existing.receiptDate || row.receipt_date < existing.receiptDate) {
          existing.receiptDate = row.receipt_date;
        }
      }

      if (row.authorized_qty !== null) {
        existing.quantityAuthorized = Math.max(
          existing.quantityAuthorized ?? 0,
          row.authorized_qty,
        );
      }

      if (!existing.imageAttached && row.has_catalog_image) {
        existing.imageAttached = Boolean(viewPictures && row.has_catalog_image);
      }
    } else {
      group.itemMap.set(itemKey, {
        individualName: row.person_name ?? null,
        phrThr: row.phr_thr ?? null,
        lin: row.lin ?? null,
        nsn: row.nsn ?? null,
        nomenclature,
        size: row.size ?? null,
        recoverableFlag: abbreviateRecoverable(row.recoverable_flag),
        cost: parseUnitPrice(row.unit_price),
        quantityIssued: row.qty_issued ?? null,
        quantityAuthorized: row.authorized_qty ?? null,
        receiptDate: row.receipt_date ?? null,
        dueDate: row.due_date ?? null,
        imageAttached: Boolean(viewPictures && row.has_catalog_image),
      });
    }

    groupsByMember.set(memberKey, group);
  });

  const groups: AssetsCurrentlyAssignedGroup[] = Array.from(groupsByMember.values()).map(
    (group) => {
      const items = Array.from(group.itemMap.values());
      let totalCost: number | null = null;

      items.forEach((item) => {
        const unitPrice = item.cost ?? null;
        const issuedQty = typeof item.quantityIssued === 'number' ? item.quantityIssued : 0;
        if (unitPrice === null || issuedQty <= 0) return;
        totalCost = (totalCost ?? 0) + unitPrice * issuedQty;
      });

      return {
        individualName: group.individualName,
        items,
        totalCost,
      };
    },
  );

  let reportTotalCost: number | null = null;
  groups.forEach((group) => {
    if (typeof group.totalCost === 'number' && Number.isFinite(group.totalCost)) {
      reportTotalCost = (reportTotalCost ?? 0) + group.totalCost;
    }
  });

  if (!groups.length) {
    reportTotalCost = null;
  }

  const headerIndividualName =
    personNames.length > 0
      ? personNames.join(', ')
      : groups.length === 1
        ? (groups[0]?.individualName ?? `All ${pl.labelPlural}`)
        : `All ${pl.labelPlural}`;

  const options: AssetsCurrentlyAssignedTemplateOptions = {
    personLabels: pl,
    generatedAt: new Date(),
    header: {
      individualName: headerIndividualName,
      totalCost: reportTotalCost,
      viewPictures,
    },
    groups,
  };

  const html = buildAssetsCurrentlyAssignedReportHtml(options);
  outputReport(
    html,
    'assets-currently-assigned.html',
    getOutputFormat(params),
    getPreOpenedWindow(params),
  );
};
